import { BrandType, getBrandTypeLabel } from "@aix/aixinternal-shared/enums"
import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface BrandManagementProps {
  onBrandSelect: (brandId: string | null) => void
}

const BrandManagement: React.FC<BrandManagementProps> = ({ onBrandSelect }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [editingBrand, setEditingBrand] = useState<any>(null)

  const { data: brands, isLoading, refetch } = trpc.contentManagement.listBrands.useQuery()
  console.log("isLoading", isLoading)
  console.log("brands", brands)
  const createBrandMutation = trpc.contentManagement.createBrand.useMutation()
  const updateBrandMutation = trpc.contentManagement.updateBrand.useMutation()

  const handleCreateBrand = async (brandData: any) => {
    try {
      await createBrandMutation.mutateAsync(brandData)
      setIsCreateModalOpen(false)
      void refetch()
    } catch (error) {
      console.error("Failed to create brand:", error)
    }
  }

  const handleUpdateBrand = async (brandId: string, updates: any) => {
    try {
      await updateBrandMutation.mutateAsync({ brandId, ...updates })
      setEditingBrand(null)
      void refetch()
    } catch (error) {
      console.error("Failed to update brand:", error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold ">Brand Management</h2>
          <p className="">Manage your brands and social media accounts</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Add Brand
        </button>
      </div>

      {/* Brands Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {brands?.map((brand: any) => (
          <div key={brand.brand.id} className="bg-background p-6 rounded-lg shadow hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium ">{brand.brand.name}</h3>
                <p className="text-sm text-gray-500 capitalize">{brand.brand.type}</p>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => setEditingBrand(brand.brand)} className="text-gray-400 hover:">
                  ✏️
                </button>
                <button onClick={() => onBrandSelect(brand.brand.id)} className=" hover:text-blue-800">
                  👁️
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Content Count:</span>
                <span className="font-medium">{brand.contentCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Accounts:</span>
                <span className="font-medium">{brand.accountCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Status:</span>
                <span className={`font-medium ${brand.brand.isActive ? "text-green-600" : "text-destructive"}`}>
                  {brand.brand.isActive ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            {brand.brand.description && <p className="text-sm  mt-3 line-clamp-2">{brand.brand.description}</p>}

            <div className="mt-4 pt-4 border-t">
              <button
                onClick={() => onBrandSelect(brand.brand.id)}
                className="w-full bg-foregroundx text-primary py-2 px-4 rounded  transition-colors"
              >
                Manage Content
              </button>
            </div>
          </div>
        ))}

        {(!brands || brands.length === 0) && (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">🏢</div>
            <h3 className="text-lg font-medium  mb-2">No brands yet</h3>
            <p className=" mb-4">Create your first brand to get started with content management</p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Brand
            </button>
          </div>
        )}
      </div>

      {/* Create Brand Modal */}
      {isCreateModalOpen && (
        <CreateBrandModal
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateBrand}
          isLoading={createBrandMutation.isLoading}
        />
      )}

      {/* Edit Brand Modal */}
      {editingBrand && (
        <EditBrandModal
          brand={editingBrand}
          onClose={() => setEditingBrand(null)}
          onSubmit={(updates) => handleUpdateBrand(editingBrand.id, updates)}
          isLoading={updateBrandMutation.isLoading}
        />
      )}
    </div>
  )
}

const CreateBrandModal: React.FC<{
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}> = ({ onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: "",
    type: BrandType.PERSONAL,
    description: "",
    isActive: true,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium  mb-4">Create New Brand</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Brand Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              placeholder="Enter brand name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Brand Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as BrandType })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              {Object.values(BrandType).map((type) => (
                <option key={type} value={type}>
                  {getBrandTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Brief description of the brand"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-primary">
              Active brand
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Creating..." : "Create Brand"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const EditBrandModal: React.FC<{
  brand: any
  onClose: () => void
  onSubmit: (data: any) => void
  isLoading: boolean
}> = ({ brand, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    name: brand.name,
    type: brand.type as BrandType,
    description: brand.description || "",
    isActive: brand.isActive,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium  mb-4">Edit Brand</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">Brand Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Brand Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as BrandType })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            >
              {Object.values(BrandType).map((type) => (
                <option key={type} value={type}>
                  {getBrandTypeLabel(type)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4  focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-primary">
              Active brand
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-primary bg-foregroundx rounded-md  transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isLoading ? "Updating..." : "Update Brand"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default BrandManagement
